import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import GroupsIcon from '@mui/icons-material/Groups';
import {
  getBookingDetailPath,
  getCustomerDetailPath,
  resolvePrimaryGuestId,
} from '../../utils/bookingDrawerLinks';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

export function getBookingGroupMeta(booking) {
  if (!booking) return null;
  const groupId = booking.group_booking_id || booking.group_booking?.group_booking_id || booking.group_booking?.id || null;
  const groupCode =
    booking.group_booking_code
    || booking.group_booking?.group_booking_code
    || booking.group_booking?.code
    || null;
  if (!groupId && !groupCode && !booking.is_group_member) return null;
  return {
    id: groupId ? Number(groupId) : null,
    code: groupCode || 'Group',
  };
}

export const getDrawerGuestLinkColor = (isDark) => (isDark ? '#ffffff' : '#000000');

const useDrawerThemeMode = (isDarkProp) => {
  const [isDark, setIsDark] = useState(() => {
    if (typeof isDarkProp === 'boolean') return isDarkProp;
    return detectThemeSync() === 'dark';
  });

  useEffect(() => {
    if (typeof isDarkProp === 'boolean') {
      setIsDark(isDarkProp);
      return undefined;
    }
    const cleanup = watchThemeChanges((theme) => {
      setIsDark(theme === 'dark');
    });
    return cleanup;
  }, [isDarkProp]);

  return isDark;
};

const linkTextSx = (linkColor, isLinked = true) => ({
  ...(isLinked ? {
    color: linkColor,
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  } : {}),
  fontWeight: 'inherit',
  fontSize: 'inherit',
  lineHeight: 'inherit',
});

export const DrawerDetailLink = ({
  to,
  children,
  variant = 'body2',
  sx = {},
  onNavigate,
  accentColor,
  isDark: isDarkProp,
  ...typographyProps
}) => {
  const isDark = useDrawerThemeMode(isDarkProp);
  const linkColor = accentColor ?? getDrawerGuestLinkColor(isDark);

  if (!to) {
    return (
      <Typography
        variant={variant}
        sx={{ color: linkColor, ...sx }}
        {...typographyProps}
      >
        {children}
      </Typography>
    );
  }

  return (
    <Typography
      component={RouterLink}
      to={to}
      variant={variant}
      onClick={() => onNavigate?.()}
      sx={{ ...sx, ...linkTextSx(linkColor, true) }}
      {...typographyProps}
    >
      {children}
    </Typography>
  );
};

export const BookingDrawerCodeChip = ({
  booking,
  bookingId,
  label,
  onNavigate,
  accentColor = CUSTOM_BORDER_COLOR,
  sx = {},
  size = 'small',
}) => {
  const path = getBookingDetailPath(booking ?? bookingId);
  const chipLabel = label ?? booking?.code ?? booking?.booking_code ?? (booking?.id ? `#${booking.id}` : '—');

  const chipSx = {
    backgroundColor: accentColor,
    color: '#000',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
    ...sx,
  };

  if (!path) {
    return <Chip label={chipLabel} size={size} sx={chipSx} />;
  }

  return (
    <Chip
      component={RouterLink}
      to={path}
      clickable
      label={chipLabel}
      size={size}
      onClick={() => onNavigate?.()}
      sx={{
        ...chipSx,
        textDecoration: 'none',
        '&:hover': {
          backgroundColor: accentColor,
          filter: 'brightness(0.95)',
        },
      }}
    />
  );
};

/**
 * Group booking indicator for drawer headers (Details / Check-in / Checkout / Allocation).
 */
export const GroupBookingIndicatorChip = ({
  booking,
  onNavigate,
  isDark = false,
  accentColor = CUSTOM_BORDER_COLOR,
  size = 'small',
  fullLabel = false,
  sx = {},
}) => {
  const group = getBookingGroupMeta(booking);
  if (!group) return null;

  const path =
    group.id && Number.isInteger(group.id) && group.id > 0
      ? `/booking/group/detail/${group.id}/`
      : null;

  const chipSx = {
    height: 24,
    fontWeight: 700,
    fontSize: '0.7rem',
    maxWidth: fullLabel ? '100%' : undefined,
    bgcolor: alpha(accentColor, isDark ? 0.2 : 0.14),
    color: isDark ? '#e2e8f0' : '#042018',
    border: `1px solid ${alpha(accentColor, 0.45)}`,
    '& .MuiChip-icon': { color: accentColor, fontSize: '0.95rem !important' },
    '& .MuiChip-label': {
      px: 0.85,
      maxWidth: fullLabel ? 'none' : 140,
      overflow: fullLabel ? 'visible' : 'hidden',
      textOverflow: fullLabel ? 'clip' : 'ellipsis',
      whiteSpace: 'nowrap',
    },
    ...sx,
  };

  if (!path) {
    return (
      <Chip
        size={size}
        icon={<GroupsIcon />}
        label={group.code}
        title={`Group booking ${group.code}`}
        sx={chipSx}
      />
    );
  }

  return (
    <Chip
      component={RouterLink}
      to={path}
      clickable
      size={size}
      icon={<GroupsIcon />}
      label={group.code}
      title={`Open group ${group.code}`}
      onClick={() => onNavigate?.()}
      sx={{
        ...chipSx,
        textDecoration: 'none',
        '&:hover': {
          bgcolor: alpha(accentColor, isDark ? 0.28 : 0.2),
        },
      }}
    />
  );
};

/**
 * Drawer header title + booking/group chips.
 * Mobile: title stays on one line; booking code on next row; group id on its own row.
 * Desktop: compact inline title + chips.
 */
export const BookingDrawerHeaderIdentity = ({
  icon,
  title,
  booking,
  bookingId,
  onNavigate,
  isDark = false,
  drawerTheme,
  accentColor = CUSTOM_BORDER_COLOR,
  label,
}) => {
  const titleColor = isDark ? (drawerTheme?.text || '#f8fafc') : '#333';
  const subtleColor = isDark ? (drawerTheme?.subtleText || '#94a3b8') : '#666';
  const group = getBookingGroupMeta(booking);
  const chipLabel = label ?? booking?.code ?? booking?.booking_code ?? (booking?.id ? `#${booking.id}` : null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.7, sm: 0 }, minWidth: 0, flex: 1, pr: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {icon}
        <Typography
          variant="h6"
          sx={{
            color: titleColor,
            fontWeight: 600,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>

        {booking && chipLabel ? (
          <Box
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body2" sx={{ color: subtleColor, mx: 0.25, flexShrink: 0 }}>
              -
            </Typography>
            <BookingDrawerCodeChip
              booking={booking}
              bookingId={bookingId}
              label={chipLabel}
              onNavigate={onNavigate}
              accentColor={accentColor}
            />
            {group ? (
              <GroupBookingIndicatorChip
                booking={booking}
                onNavigate={onNavigate}
                isDark={isDark}
                accentColor={accentColor}
              />
            ) : null}
          </Box>
        ) : null}
      </Box>

      {booking && chipLabel ? (
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.65,
            width: '100%',
            minWidth: 0,
          }}
        >
          <BookingDrawerCodeChip
            booking={booking}
            bookingId={bookingId}
            label={chipLabel}
            onNavigate={onNavigate}
            accentColor={accentColor}
            sx={{
              maxWidth: '100%',
              height: 24,
              '& .MuiChip-label': {
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
          {group ? (
            <GroupBookingIndicatorChip
              booking={booking}
              onNavigate={onNavigate}
              isDark={isDark}
              accentColor={accentColor}
              fullLabel
            />
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export const DrawerGuestFieldLink = ({
  booking,
  guest,
  guestId,
  children,
  variant = 'body2',
  sx = {},
  onNavigate,
  accentColor,
  isDark: isDarkProp,
  ...typographyProps
}) => {
  const resolvedGuestId = guestId ?? resolvePrimaryGuestId(booking, guest);
  const to = getCustomerDetailPath(resolvedGuestId);
  const isLinked = Boolean(to);

  return (
    <DrawerDetailLink
      to={to}
      variant={variant}
      sx={{
        ...(isLinked ? { cursor: 'pointer' } : {}),
        ...sx,
      }}
      onNavigate={onNavigate}
      accentColor={accentColor}
      isDark={isDarkProp}
      {...typographyProps}
    >
      {children}
    </DrawerDetailLink>
  );
};

export const DrawerGuestNamePhone = ({
  guestName,
  guestPhone,
  guest,
  booking,
  onNavigate,
  nameVariant = 'body2',
  phoneVariant = 'caption',
  nameSx = {},
  phoneSx = {},
  isDark: isDarkProp,
}) => (
  <>
    <DrawerGuestFieldLink
      booking={booking}
      guest={guest}
      variant={nameVariant}
      sx={nameSx}
      onNavigate={onNavigate}
      isDark={isDarkProp}
    >
      {guestName}
    </DrawerGuestFieldLink>
    {guestPhone ? (
      <DrawerGuestFieldLink
        booking={booking}
        guest={guest}
        variant={phoneVariant}
        sx={phoneSx}
        onNavigate={onNavigate}
        isDark={isDarkProp}
      >
        {guestPhone}
      </DrawerGuestFieldLink>
    ) : null}
  </>
);
